import type { UseListingReturn } from '@shopware/composables'
import type { RequestParameters } from '#shopware'

/**
 * Shared listing context for category and search listing components.
 * Extracts the repeated inject/composable pattern used in filter components.
 */
export const useListingContext = (listingType: 'category' | 'search' = 'category') => {
    const categoryListing = inject<UseListingReturn | undefined>('categoryListing', undefined)
    const searchListing = useProductSearchListing()

    const isSearchListing = computed(() => listingType === 'search')

    const getInitialFilters = computed(() =>
        isSearchListing.value
            ? searchListing.getInitialFilters.value
            : (categoryListing?.getInitialFilters.value ?? [])
    )

    const getCurrentSortingOrder = computed(() =>
        isSearchListing.value
            ? searchListing.getCurrentSortingOrder.value
            : categoryListing?.getCurrentSortingOrder.value
    )

    const getCurrentListing = computed(() =>
        isSearchListing.value
            ? searchListing.getCurrentListing.value
            : categoryListing?.getCurrentListing.value
    )

    const filtersToQuery = (criteria: RequestParameters<'searchPage'>) =>
        isSearchListing.value
            ? searchListing.filtersToQuery(criteria)
            : (categoryListing?.filtersToQuery(criteria) ?? {})

    const doSearch = (criteria: RequestParameters<'searchPage'>) =>
        isSearchListing.value
            ? searchListing.search(criteria)
            : (categoryListing?.search(criteria) ?? Promise.resolve())

    return {
        categoryListing,
        searchListing,
        isSearchListing,
        getInitialFilters,
        getCurrentSortingOrder,
        getCurrentListing,
        filtersToQuery,
        doSearch,
    }
}